import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AllInboxIcon from "@material-ui/icons/AllInbox";
import { Button, Popover, Tooltip } from "antd";
import { widgetHoverAtom } from "atoms/interaction";
import classnames from "classnames";
import { IS_BOX_PLOT_CHART } from "constants/chart.constants";
import { useChartsContext } from "contexts/ChartContext";
import {
  BoxPlotDisplayMode,
  resolveBoxPlotDisplayMode
} from "entities/charts/boxPlotDisplayMode";
import { useAtomValue } from "jotai";
import { updateChartByPath } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components/macro";
import { supportsMaxBins } from "utils/groupBy/supportsMaxBins";

import { useChartEntities } from "hooks/charts";

import { EntityKind } from "models/entityKind";

import { visibilityToggleStyles } from "../../shared/SharedStyles";
import ChartBinsSettings from "./ChartBinsSettings";

const xAxisTitle = (options: string): string => {
  try {
    const name = JSON.parse(options || "{}")?.xAxis?.name;
    return typeof name === "string" ? name.replace(/\{sub\|([^}]*)\}/g, "$1") : "";
  } catch {
    return "";
  }
};

/**
 * The box and violin x-axis bin override, beside the x-axis title. The categories are
 * always the focus groups; this only swaps the focus Max Bins, per display mode.
 */
function BoxPlotBinSettingsToggle() {
  const dispatch = useDispatch();
  const { id, entityKind } = useChartsContext();
  const {
    chartType,
    options,
    screenshot,
    dataTable,
    chartFocus,
    jitterPlotToggle,
    violinPlotToggle,
    boxPlotBins
  } = useChartEntities(id);
  // Only the focus group by: the wider chart dependencies re-render on every well selection.
  const groupBy = useSelector((state: RootState) =>
    entityKind === EntityKind.Well
      ? state.groupBy.globalGroupBy
      : state.groupBy.globalFacilityFocus
  );
  const isHover = useAtomValue(widgetHoverAtom)?.[id] ?? false;
  const [open, setOpen] = useState(false);

  const mode = resolveBoxPlotDisplayMode({
    jitterActive: jitterPlotToggle?.active,
    violinActive: violinPlotToggle?.active
  });
  const focus = chartFocus?.active ? chartFocus.properties?.groupBy : groupBy;

  const shown =
    IS_BOX_PLOT_CHART(chartType) &&
    mode !== BoxPlotDisplayMode.Jitter &&
    supportsMaxBins(focus?.canBin, focus?.dataType);
  // The options string carries every data point, so it's only parsed while the button shows.
  const title = useMemo(() => (shown ? xAxisTitle(options) : ""), [shown, options]);

  if (!shown) return null;

  const setting = boxPlotBins?.properties?.[mode];
  const isActive = setting?.active ?? false;
  const visible = (isHover || open) && !screenshot?.active && !dataTable?.active;

  return (
    <Wrapper
      className={classnames({ visible })}
      data-testid="box-plot-bin-settings-anchor">
      <TitleSpacer aria-hidden>{title}</TitleSpacer>
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        placement="top"
        content={
          <ChartBinsSettings
            active={isActive}
            maxBins={setting?.maxBins}
            onChange={(active, maxBins) =>
              dispatch(
                updateChartByPath({
                  id,
                  path: `features.boxPlotBins.properties.${mode}`,
                  value: { active, maxBins }
                })
              )
            }
            testIdPrefix="box-plot"
          />
        }>
        <Tooltip title="Bin Settings">
          <BinButton
            data-testid="box-plot-bin-settings-trigger"
            aria-label="Bin Settings"
            aria-pressed={isActive}
            size="small"
            $active={isActive}
            icon={<AllInboxIcon />}
          />
        </Tooltip>
      </Popover>
    </Wrapper>
  );
}

export default BoxPlotBinSettingsToggle;

// Centred on the x-axis title as ChartAxisFieldSelector's x field picker is, so the
// button lands just past the title's end.
const Wrapper = styled.div`
  ${visibilityToggleStyles};
  position: absolute;
  z-index: 1;
  bottom: 0;
  left: 50%;
  transform: translateX(calc(24px - 50%));

  /* Only the button takes the pointer, never the title it sits beside. */
  &.visible {
    pointer-events: none;
  }
`;

const TitleSpacer = styled.span`
  display: inline-block;
  visibility: hidden;
  padding: 2px 12px;
  border: 1px solid transparent;
  font-size: 15px;
  font-weight: var(--fontWeightMedium);
  white-space: nowrap;
`;

const BinButton = styled(Button)<{ $active: boolean }>`
  position: absolute;
  top: 50%;
  left: calc(100% + 4px);
  transform: translateY(-50%);
  pointer-events: auto;
  display: grid;
  justify-content: center;
  align-items: center;
  color: ${(p) => (p.$active ? "var(--color-primary)" : "var(--color-text)")};
  border-color: ${(p) => (p.$active ? "var(--color-primary)" : undefined)};
`;
